import { captureException } from '@sentry/react';
import { useLocation } from 'react-router-dom';

import { useSnackBarContext } from '~/features/snackbar/SnackbarContext';
import i18n from '~/i18n/config';
import analytics from '~/services/analytics';
import { isExpectedError } from '~/services/errorReporting';

export type UserErrorData = {
  error: any;
  method: string;
  userMessage: string;
  isReportButton?: boolean;
  isFullWidth?: boolean;
  autoHideDuration?: number;
};

const { t } = i18n;

const useErrors = (): {
  reportUserErrors: (data: UserErrorData) => void;
  reportProfileError: (e) => void;
} => {
  const { showSnackbar } = useSnackBarContext();
  const { pathname } = useLocation();

  const reportUserErrors = (data: UserErrorData): void => {
    const {
      error,
      method,
      userMessage,
      isReportButton = true,
      isFullWidth = false,
      autoHideDuration = !isReportButton ? 3000 : 10000,
    } = data;

    const errorCode = error.status || 'Code :(';
    const errorMessage = error.error || error.message || 'Error :(';
    const eventProperties = {
      error_method: method,
      error_text: errorMessage,
      error_code: errorCode,
      page: pathname,
    };

    showSnackbar(
      errorMessage === 'Server Error'
        ? t('server_error', {
            ns: 'error',
            defaultValue:
              'Server error encountered. Please try again later or reach out to support or report issue',
          })
        : userMessage,
      'report',
      autoHideDuration,
      '',
      () => {
        analytics.trackEvent('error - error bar close', eventProperties);
      },
      () => {
        analytics.trackEvent('error - error bar click', eventProperties);
      },
      () => {
        analytics.trackEvent('error - error bar view', eventProperties);
      },
      isFullWidth,
      isReportButton,
    );
    // isReportButton only renders the snackbar button, so read the error.
    if (isExpectedError(error, errorMessage)) {
      return;
    }

    captureException(new Error(errorMessage), {
      level: 'error',
      tags: {
        error_method: method,
        error_text: errorMessage,
        error_code: errorCode,
        page: pathname,
      },
    });
  };

  const reportProfileError = (e: any): void => {
    reportUserErrors({
      error: e,
      method: 'getProfile',
      userMessage: t('get_profile', {
        ns: 'error',
        defaultValue:
          'Error retrieving profile data. Please refresh the page or report issue',
      }),
    });
  };

  return { reportUserErrors, reportProfileError };
};

export default useErrors;
